/**
 * Carrying a half-finished signup out of an app's in-app browser and into a real one.
 *
 * Google sign-in cannot complete inside TikTok's browser. Google refuses to treat an embedded
 * WebView as a trusted session — it will not offer the accounts already signed in, and the
 * credential it hands back cannot be read on the return leg, because the WebView's storage is
 * partitioned away from the one the outbound leg wrote to. Measured on an iPhone: sign-in
 * succeeded at Google's end and the browser then sat on `/__/auth/handler` forever, blank.
 * That is a platform position, not a bug we can fix, so the only working answer is to leave.
 *
 * **The state has to travel in the URL.** Nothing else survives the jump. The onboarding
 * answers live in sessionStorage (utils/onboarding.js) and the referral code in localStorage
 * (utils/referral.js), and a different browser shares neither. Bouncing someone to Safari
 * without carrying both would make them answer twenty questions again AND silently drop the
 * creator's commission -- the referral would simply not exist on the other side.
 *
 * Deliberately no server round trip. A token would mean an endpoint, a store and an expiry to
 * get wrong, for a payload that is a few hundred characters of the user's own answers.
 */

import { getOnboarding, setOnboarding } from './onboarding';
import { readReferral } from './referral';

/** The signup URL the handoff lands on, matching what Onboarding's finish() navigates to. */
const SIGNUP_PATH = '/signup?redirect=/abandoned-patents&from=onboarding';

/** Query parameter carrying the encoded onboarding answers. */
const PARAM = 'ob';

/**
 * A URL cannot be relied on past about 2,000 characters, and a truncated one is worse than a
 * short one: it arrives looking valid and decodes to nothing. Past this the answers are
 * dropped and the referral still travels, because a creator being paid matters more than
 * somebody re-picking their industries.
 */
const MAX_PAYLOAD = 1200;

function toBase64Url(text) {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  // A loop rather than String.fromCharCode(...bytes): spreading a large array overflows the
  // call stack, and this runs on whatever the answers happen to be.
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(encoded) {
  const padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * The absolute link to hand to a real browser.
 *
 * The referral rides as `?ref=`, the same parameter a creator's own link uses, so the
 * receiving side needs no new code to read it -- captureReferralFromUrl in App.jsx already
 * does, on every route.
 */
export function buildHandoffUrl() {
  const url = new URL(SIGNUP_PATH, window.location.origin);

  const code = readReferral();
  if (code) url.searchParams.set('ref', code);

  try {
    const answers = getOnboarding();
    if (answers && Object.keys(answers).length > 0) {
      const payload = toBase64Url(JSON.stringify(answers));
      if (payload.length <= MAX_PAYLOAD) url.searchParams.set(PARAM, payload);
    }
  } catch {
    // Unencodable answers are not worth losing the referral over.
  }

  return url.toString();
}

/**
 * Put the answers back on arrival, before anything reads them.
 *
 * Merged rather than assigned, and only into an empty slot: someone who already has answers in
 * this browser is mid-flow here, and a stale link opened in a second tab must not overwrite
 * what they are in the middle of.
 *
 * Returns whether anything was restored, for logging. Never throws -- a bad payload means a
 * mangled link, and the signup form behind it still works.
 */
export function restoreHandoffFromUrl(search = window.location.search) {
  try {
    const encoded = new URLSearchParams(search).get(PARAM);
    if (!encoded) return false;

    const existing = getOnboarding();
    if (existing && Object.keys(existing).length > 0) return false;

    const answers = JSON.parse(fromBase64Url(encoded));
    if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return false;

    setOnboarding(answers);
    return true;
  } catch {
    return false;
  }
}

/**
 * An Android intent URL, which opens Chrome directly rather than asking politely.
 *
 * Android is the platform where this can actually be automated; iOS has no equivalent and gets
 * instructions instead. `browser_fallback_url` covers a device with no Chrome, where the intent
 * would otherwise fail silently.
 */
export function androidChromeIntent(url) {
  const withoutScheme = url.replace(/^https?:\/\//, '');
  return (
    `intent://${withoutScheme}#Intent;scheme=https;package=com.android.chrome;` +
    `S.browser_fallback_url=${encodeURIComponent(url)};end`
  );
}
